import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { AlertTriangle, Flame, Zap } from 'lucide-react';
import { LiveNumber } from './LiveNumber';
interface UrgencySectionProps {
  copiesLeft: number;
  claimed: number;
  totalCopies: number;
  claimPulse: number;
}
const recentBuyers = ['Alguien en Ciudad de México acaba de reclamar una copia', 'Alguien en Madrid acaba de reclamar una copia', 'Alguien en Bogotá acaba de reclamar una copia', 'Alguien en Buenos Aires acaba de reclamar una copia', 'Alguien en Santiago acaba de reclamar una copia', 'Alguien en Lima acaba de reclamar una copia', 'Alguien en Guadalajara acaba de reclamar una copia', 'Alguien en Medellín acaba de reclamar una copia'];
export function UrgencySection({
  copiesLeft,
  claimed,
  totalCopies,
  claimPulse
}: UrgencySectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const percent = claimed / totalCopies * 100;
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (claimPulse === 0) return;
    setNotice(recentBuyers[(claimPulse - 1) % recentBuyers.length]);
    const timer = window.setTimeout(() => setNotice(null), 4200);
    return () => window.clearTimeout(timer);
  }, [claimPulse]);
  return <section className="border-y border-crimson-500/25 bg-wine-900/40 py-12 sm:py-20" data-id="element-283" data-mp="37kdon6wmp2nl">
      <div className="container" data-id="element-284" data-mp="3cjq9xn7uq1o2">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 26
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-70px'
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} className="mx-auto max-w-2xl text-center" data-id="element-285" data-mp="3giz58wiji7lq">
          <span className="inline-flex items-center gap-2.5 rounded-full border border-crimson-500/45 bg-crimson-600/15 px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest2 text-crimson-500" data-id="element-286" data-mp="1r4ucqhj5to4d">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" data-id="element-287" data-mp="1o1xvms4ula4j" />
            Urgencia
          </span>
          <h2 className="mt-5 font-display text-[1.7rem] font-bold leading-tight text-cream sm:mt-6 sm:text-4xl" data-id="element-288" data-mp="1k25w14xfmebb">
            Este precio NO dura para siempre
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-[15px] leading-relaxed text-cream/65 sm:mt-5 sm:text-base" data-id="element-289" data-mp="1t22cwkdky8vt">
            Solo quedan{' '}
            <span className="font-semibold text-crimson-500" data-id="element-290" data-mp="2709118sq8uia">
              <LiveNumber value={copiesLeft} data-id="element-291" data-mp="230i56qocfo6w" /> copias
            </span>{' '}
            al precio de $9.99. Cuando se agoten, el precio vuelve a $137.
          </p>

          <div className="relative mt-7 rounded-2xl border border-cream/10 bg-noir-900/70 p-5 sm:mt-9 sm:p-6" data-id="element-292" data-mp="2gthn0zblluhm">
            <div className="flex items-end justify-between text-xs font-semibold uppercase tracking-wider" data-id="element-293" data-mp="2qpklnv78razt">
              <span className="flex items-center gap-2 text-cream/50" data-id="element-294" data-mp="2lqq3mvh3lqgr">
                <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-crimson-500 animate-pulseDot" data-id="element-295" data-mp="2lodenycxafu7" />
                Copias reclamadas · en vivo
              </span>
              <span className="flex items-center gap-1 text-gold-400" data-id="element-296" data-mp="2vec6a05ix3q9">
                <LiveNumber value={claimed} className="font-bold" data-id="element-297" data-mp="2vg6hgmqzthh5" /> de {totalCopies}
              </span>
            </div>

            <div role="progressbar" aria-valuemin={0} aria-valuemax={totalCopies} aria-valuenow={claimed} aria-label={`${claimed} de ${totalCopies} copias ya reclamadas`} className="relative mt-3.5 h-3.5 w-full overflow-hidden rounded-full bg-noir-700 ring-1 ring-inset ring-cream/10" data-id="element-298" data-mp="2loiw93l14wo4">
              <motion.div animate={{
              width: inView ? `${percent}%` : '0%'
            }} transition={{
              duration: 1.2,
              ease: [0.22, 1, 0.36, 1]
            }} className="relative h-full rounded-full bg-crimson-600 shadow-[0_0_18px_rgba(220,36,55,0.6)]" data-id="element-299" data-mp="2hkb2ut3mmsjk">
                <motion.span key={claimPulse} aria-hidden="true" initial={{
                opacity: 0.85,
                scaleY: 1.6
              }} animate={{
                opacity: 0,
                scaleY: 1
              }} transition={{
                duration: 0.9,
                ease: 'easeOut'
              }} className="absolute right-0 top-0 h-full w-10 rounded-full bg-white" data-id="element-300" data-mp="3b184qe74l30s" />
                <span aria-hidden="true" className="absolute right-0 top-1/2 h-3.5 w-3.5 -translate-y-1/2 translate-x-1/2 rounded-full bg-crimson-500 ring-2 ring-white/70" data-id="element-301" data-mp="2rcr0ufl73xro" />
              </motion.div>
            </div>

            <div className="mt-3.5 flex min-h-[20px] items-center justify-between gap-3 text-xs" data-id="element-302" data-mp="2dcolb00d1gfl">
              <span className="flex items-center gap-1.5 text-cream/45" data-id="element-303" data-mp="gibp6tmmmpec">
                <Flame className="h-3.5 w-3.5 text-crimson-500" aria-hidden="true" data-id="element-304" data-mp="bjs22pwar7ke" />
                <LiveNumber value={copiesLeft} data-id="element-305" data-mp="j3syh5kc3vo4" /> disponibles al precio de lanzamiento
              </span>
              <span className="font-semibold tabular-nums text-cream/40" data-id="element-306" data-mp="o2mccz3j9oot">
                {Math.round(percent)}%
              </span>
            </div>

            <div className="pointer-events-none absolute inset-x-0 -bottom-3 flex justify-center" data-id="element-307" data-mp="su48n9um313g">
              <AnimatePresence data-id="element-308" data-mp="xs5qjrgawudc">
                {notice && <motion.p key={notice + claimPulse} initial={{
                opacity: 0,
                y: 8,
                scale: 0.96
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -6,
                scale: 0.98
              }} transition={{
                duration: 0.35,
                ease: [0.22, 1, 0.36, 1]
              }} className="flex items-center gap-2 rounded-full border border-crimson-500/40 bg-noir-950 px-4 py-1.5 text-[11px] font-semibold text-crimson-500 shadow-[0_10px_25px_-12px_rgba(0,0,0,0.9)]" data-id="element-309" data-mp="5qe542qemwn">
                    <Zap className="h-3 w-3" aria-hidden="true" data-id="element-310" data-mp="3mkivy8f9fe6a" />
                    {notice}
                  </motion.p>}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}